import { useEffect } from 'react'

const PreviousActionsQuestion = ({ formData, setFormData, questionState, setQuestionState, validationErrors = [] }) => {

    useEffect(() => {
        const existingResponses = formData.form_responses?.question3?.responses || [];
        if (existingResponses.length > 0) {
            const newState = { ...questionState };

            existingResponses.forEach(parentResponse => {
                if (parentResponse.sub_responses && Array.isArray(parentResponse.sub_responses)) {
                    parentResponse.sub_responses.forEach(response => {
                        if (newState[response.answer_text]) {
                            newState[response.answer_text].selected = true;

                            if (response.text_response) {
                                newState[response.answer_text].text_response = response.text_response;
                            }
                        }
                    });
                }
            });

            setQuestionState(newState);
        }
    }, [formData.form_responses?.question3?.responses])

    const meetingsResponses = [
        'Return To Work Meeting',
        'Welfare (Attendance) Meeting',
        'HR Capability Meeting',
        'Informal Check In',
        'Other meetings'
    ];

    const outcomesResponses = [
        'Attendance Plan Agreed',
        'Performance Management Plan Agreed',
        'Adjustments Agreed',
        'Sickness Absence',
        'Escalation In Line With Policy',
        'No Improvement',
        'Other outcomes'
    ];

    const handleQuestionUpdate = (responseKey, checkedValue, parentKey = null, dataKey = null, value = null, isMeeting = false) => {
        setQuestionState(prevState => {
            const newState = { ...prevState };

            if (dataKey) {
                newState[responseKey] = {
                    ...newState[responseKey],
                    [dataKey]: value
                };
            } else {
                newState[responseKey] = {
                    ...newState[responseKey],
                    selected: checkedValue
                };
            }

            return newState;
        });

        setFormData(prevFormData => {
            const currentResponses = prevFormData.form_responses?.question3?.responses || [];

            // Initialize default structure if it doesn't exist
            let responses = [...currentResponses];
            if (responses.length === 0) {
                responses = [
                    {
                        answer_text: "Meetings Held",
                        sub_responses: []
                    },
                    {
                        answer_text: "Outcomes",
                        sub_responses: []
                    }
                ];
            }

            if (!responses.find(r => r.answer_text === "Meetings Held")) {
                responses.unshift({
                    answer_text: "Meetings Held",
                    sub_responses: []
                });
            }
            if (!responses.find(r => r.answer_text === "Outcomes")) {
                responses.push({
                    answer_text: "Outcomes",
                    sub_responses: []
                });
            }

            const parentKey = isMeeting ? "Meetings Held" : "Outcomes";
            const parentIndex = responses.findIndex(r => r.answer_text === parentKey);
            const targetSection = [...responses[parentIndex].sub_responses];

            if (dataKey) {
                const updatedSection = targetSection.map(response =>
                    response.answer_text === responseKey
                        ? { ...response, [dataKey]: value }
                        : response
                );

                const newResponses = [...responses];
                newResponses[parentIndex] = {
                    ...newResponses[parentIndex],
                    sub_responses: updatedSection
                };

                return {
                    ...prevFormData,
                    form_responses: {
                        ...prevFormData.form_responses,
                        question3: {
                            question_title: "What has happened so far?",
                            responses: newResponses
                        }
                    }
                };
            } else {
                let updatedSection;

                if (checkedValue) {
                    if (!targetSection.find(r => r.answer_text === responseKey)) {
                        updatedSection = [...targetSection, {
                            answer_text: responseKey,
                            sub_responses: []
                        }];
                    } else {
                        updatedSection = targetSection;
                    }
                } else {
                    updatedSection = targetSection.filter(r => r.answer_text !== responseKey);
                }

                const newResponses = [...responses];
                newResponses[parentIndex] = {
                    ...newResponses[parentIndex],
                    sub_responses: updatedSection
                };

                return {
                    ...prevFormData,
                    form_responses: {
                        ...prevFormData.form_responses,
                        question3: {
                            question_title: "What has happened so far?",
                            responses: newResponses
                        }
                    }
                };
            }
        });
    };

    const hasResponse = (responseKey) => {
        return questionState[responseKey]?.selected || false;
    };

    const getResponseData = (responseKey, dataKey) => {
        return questionState[responseKey]?.[dataKey] || '';
    };
    return (
        <div className="mt-6 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-t-lg">
                <h2 className="text-lg font-semibold text-gray-800">
                    Step 4: What has happened so far?
                </h2>
                <span className="text-red-500 text-sm font-medium">Required *</span>
            </div>
            <div>
                {(validationErrors.includes('meetings_held') || validationErrors.includes('outcomes')) && (
                    <div className="bg-red-50 border-l-4 border-red-400 p-3 mx-4 mt-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">
                                    Please select at least one option in both "Meetings held" and "Outcomes" sections.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                <div className="p-2">
                    <div className="space-y-4">
                        <div className={`${validationErrors.includes('meetings_held') ? 'border-2 border-red-200 bg-red-50 p-3 rounded-md' : ''}`}>
                            <label className="block text-sm font-medium text-gray-600 mb-2">
                                Meetings held:
                            </label>
                            <div className="space-y-2 ml-4">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="return_meeting"
                                        value="Return To Work Meeting"
                                        className="mr-2"
                                        onChange={(e) => handleQuestionUpdate('Return To Work Meeting', e.target.checked, null, null, null, true)}
                                        checked={hasResponse('Return To Work Meeting')}
                                    />
                                    <label htmlFor="return_meeting" className="text-sm text-gray-700">Return To Work Meeting</label>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="welfare_meeting"
                                        value="Welfare (Attendance) Meeting"
                                        className="mr-2"
                                        onChange={(e) => handleQuestionUpdate('Welfare (Attendance) Meeting', e.target.checked, null, null, null, true)}
                                        checked={hasResponse('Welfare (Attendance) Meeting')}
                                    />
                                    <label htmlFor="welfare_meeting" className="text-sm text-gray-700">Welfare (Attendance) Meeting</label>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="hr_meeting"
                                        value="HR Capability Meeting"
                                        className="mr-2"
                                        onChange={(e) => handleQuestionUpdate('HR Capability Meeting', e.target.checked, null, null, null, true)}
                                        checked={hasResponse('HR Capability Meeting')}
                                    />
                                    <label htmlFor="hr_meeting" className="text-sm text-gray-700">HR Capability Meeting</label>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="informal_checkin"
                                        value="Informal Check In"
                                        className="mr-2"
                                        onChange={(e) => handleQuestionUpdate('Informal Check In', e.target.checked, null, null, null, true)}
                                        checked={hasResponse('Informal Check In')}
                                    />
                                    <label htmlFor="informal_checkin" className="text-sm text-gray-700">Informal Check In</label>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="no_meetings_yet"
                                        value="No Meetings Yet"
                                        className="mr-2"
                                        onChange={(e) => handleQuestionUpdate('No Meetings Yet', e.target.checked, null, null, null, true)}
                                        checked={hasResponse('No Meetings Yet')}
                                    />
                                    <label htmlFor="no_meetings_yet" className="text-sm text-gray-700">No Meetings Yet</label>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="other_meetings"
                                        value="Other meetings"
                                        className="mr-2"
                                        onChange={(e) => handleQuestionUpdate('Other meetings', e.target.checked, null, null, null, true)}
                                        checked={hasResponse('Other meetings')}
                                    />
                                    <label htmlFor="other_meetings" className="text-sm text-gray-700">Other Relevant Meetings (Please State)</label>
                                </div>
                            </div>
                            {hasResponse('Other meetings') && (
                                <div className="mt-2 ml-4">
                                    <textarea
                                        id="meetingOther"
                                        name="meetingOther"
                                        rows="2"
                                        className="border rounded-md py-2 px-4 w-full resize-vertical"
                                        placeholder="Please specify other meetings..."
                                        onChange={(e) => handleQuestionUpdate('Other meetings', null, null, 'text_response', e.target.value, true)}
                                        value={getResponseData('Other meetings', 'text_response')}
                                    ></textarea>
                                </div>
                            )}
                        </div>

                        <div className={`${validationErrors.includes('outcomes') ? 'border-2 border-red-200 bg-red-50 p-3 rounded-md' : ''}`}>
                            <label className="block text-sm font-medium text-gray-600 mb-2">
                                Outcomes:
                            </label>
                            <div className="space-y-2 ml-4">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="attendance_plan"
                                        value="Attendance Plan Agreed"
                                        className="mr-2"
                                        onChange={(e) => handleQuestionUpdate('Attendance Plan Agreed', e.target.checked)}
                                        checked={hasResponse('Attendance Plan Agreed')}
                                    />
                                    <label htmlFor="attendance_plan" className="text-sm text-gray-700">Attendance Plan Agreed</label>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="performance_plan"
                                        value="Performance Management Plan Agreed"
                                        className="mr-2"
                                        onChange={(e) => handleQuestionUpdate('Performance Management Plan Agreed', e.target.checked)}
                                        checked={hasResponse('Performance Management Plan Agreed')}
                                    />
                                    <label htmlFor="performance_plan" className="text-sm text-gray-700">Performance Management Plan Agreed</label>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="adjustments_agreed"
                                        value="Adjustments Agreed"
                                        className="mr-2"
                                        onChange={(e) => handleQuestionUpdate('Adjustments Agreed', e.target.checked)}
                                        checked={hasResponse('Adjustments Agreed')}
                                    />
                                    <label htmlFor="adjustments_agreed" className="text-sm text-gray-700">Adjustments Agreed</label>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="sickness_absence"
                                        value="Sickness Absence"
                                        className="mr-2"
                                        onChange={(e) => handleQuestionUpdate('Sickness Absence', e.target.checked)}
                                        checked={hasResponse('Sickness Absence')}
                                    />
                                    <label htmlFor="sickness_absence" className="text-sm text-gray-700">Sickness Absence</label>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="escalation_policy"
                                        value="Escalation In Line With Policy"
                                        className="mr-2"
                                        onChange={(e) => handleQuestionUpdate('Escalation In Line With Policy', e.target.checked)}
                                        checked={hasResponse('Escalation In Line With Policy')}
                                    />
                                    <label htmlFor="escalation_policy" className="text-sm text-gray-700">Escalation In Line With Policy</label>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="no_improvement"
                                        value="No Improvement"
                                        className="mr-2"
                                        onChange={(e) => handleQuestionUpdate('No Improvement', e.target.checked)}
                                        checked={hasResponse('No Improvement')}
                                    />
                                    <label htmlFor="no_improvement" className="text-sm text-gray-700">No Improvement</label>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="other_outcomes"
                                        value="Other outcomes"
                                        className="mr-2"
                                        onChange={(e) => handleQuestionUpdate('Other outcomes', e.target.checked)}
                                        checked={hasResponse('Other outcomes')}
                                    />
                                    <label htmlFor="other_outcomes" className="text-sm text-gray-700">Other Relevant Outcomes (Please State)</label>
                                </div>
                            </div>
                            {hasResponse('Other outcomes') && (
                                <div className="mt-2 ml-4">
                                    <textarea
                                        id="outcomeOther"
                                        name="outcomeOther"
                                        rows="2"
                                        className="border rounded-md py-2 px-4 w-full resize-vertical"
                                        placeholder="Please specify other outcomes..."
                                        onChange={(e) => handleQuestionUpdate('Other outcomes', null, null, 'text_response', e.target.value)}
                                        value={getResponseData('Other outcomes', 'text_response')}
                                    ></textarea>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PreviousActionsQuestion;